//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isAfter } from 'date-fns';
import styled from '@emotion/styled';
import { Box, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { JobDetail, UserProfile } from '@/redux/slices/slice_types';
import { getJob, updateJobDueDate, updateJobEmbargoDate } from '@/redux/slices/jobSlice';
import { applyFilter, clearFilters, setCurrentTable } from '@/redux/slices/dataTableSlice';
import { selectCanReadProfiles, selectCanWriteJobs } from '@/redux/selectors/authSelectors';
import { selectJob, selectJobIsLoading } from '@/redux/selectors/jobSelectors';
import { selectBulkActionConstants } from '@/redux/selectors/dataTableSelectors';

import { filterEmbargoMinAndMaxTime, findandFormatUTCDateInString } from '@/_utils/date';
import { convertStylesToStyled, displayBulkActionIdent, getLineageMetaTitle, insertWordBreaks } from '@/_utils/utils';
import { useToast } from '@/components/_common/Toast';
import { Breadcrumbs, LabelValue, TitleBar } from '@/components/_common';
import PolarisIcon, { IconDictionary } from '@/components/_common/PolarisIcon';
import LinkButton from '@/components/_common/LinkButton';
import Refresh from '@/components/_common/Refresh';
import JobTypeLabel from '@/components/Job/JobTypeLabel';
import AssigneeSelect from '@/components/_common/AssigneeSelect/AssigneeSelect';
import DateTimeDisplay from '@/components/_common/DateTimeDisplay';
import EditableDate from '@/components/_common/EditableDate';
import StatusDisplay from '@/components/_common/StatusDisplay';
import CancelJobDialog from '@/components/_common/CancelJobDialog';
import Notes from '@/components/_common/Notes/Notes';
import BundleIdBox from '@/components/Jobs/BundleIdBox';
import EntityActionDialog from '@/components/_common/EntityActionDialog';
import { useTranslation } from 'react-i18next';

type JobTitleBarProps = {
  onRefresh: () => void;
};

const JobTitleBar = ({ onRefresh }: JobTitleBarProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { error } = useToast();
  const data: JobDetail = useAppSelector(selectJob);
  const loading = useAppSelector(selectJobIsLoading);
  const canViewProfiles = useAppSelector(selectCanReadProfiles) && !data?.profile?.deleted_at;
  const fromOrderPage = location?.pathname?.includes('orders/');
  const canWriteJobs = useAppSelector(selectCanWriteJobs);
  const actions = useAppSelector(selectBulkActionConstants)?.jobs;
  const actionsByStatusStage = useMemo(() => {
    if (!actions) return {};
    const bulkActionsByStatusStage = {} as any;
    Object.keys(actions).forEach((action) => {
      actions[action].allowed_statuses.forEach((status) => {
        if (!bulkActionsByStatusStage[status.status]) {
          bulkActionsByStatusStage[status.status] = { stage: {}, actions: [] };
        }
        if (!status.stage) return bulkActionsByStatusStage[status.status].actions.push(action);

        if (!bulkActionsByStatusStage[status.status].stage[status.stage]) {
          bulkActionsByStatusStage[status.status].stage[status.stage] = [action];
        } else {
          bulkActionsByStatusStage[status.status].stage[status.stage].push(action);
        }
      });
    });
    return bulkActionsByStatusStage;
  }, [actions]);
  const customFields = useContext(ConfigContext)?.jobDetails?.titleBar?.(data);
  const ignoreBulkActions = useContext(ConfigContext)?.jobDetails?.ignoreBulkActions;
  const bulkActionIcons = useContext(ConfigContext)?.bulkActionIcons;

  const [dialogAction, setDialogAction] = useState<string | null>(null);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const {
    id,
    hid,
    order,
    description,
    customer,
    stage,
    status,
    job_type,
    created_at,
    due_date: dueDate,
    service_meta,
    profile,
    embargo_date,
    embargo_resumed_at,
    title,
    tasks,
    status_reason,
    managed_by: jobManagedBy,
    bundle_job_hid
  } = data;
  const due_date = dueDate || service_meta?.job?.due_date;
  const disableCancelJob = status === 'complete' || status === 'cancelled';
  const isEmbargoDateDisabled = !due_date || new Date(due_date).getTime() < Date.now();
  const managed_by = useMemo(
    () => (typeof jobManagedBy !== 'string' && jobManagedBy !== undefined ? jobManagedBy : order?.managed_by),
    [jobManagedBy, order?.managed_by]
  );
  const isActionValid = (action: string) => {
    if (ignoreBulkActions?.includes(action)) {
      return false;
    }

    return actionsByStatusStage[status]?.actions.includes(action) || actionsByStatusStage[status]?.stage[stage]?.length;
  };

  const onJobEdit = async (key: any, value: any) => {
    if (key === 'due_date') {
      try {
        if (embargo_date && isAfter(new Date(embargo_date), new Date(value))) {
          const updateDueDatePromise = dispatch(updateJobDueDate({ id, value }));
          const updateEmbargoDatePromise = dispatch(updateJobEmbargoDate({ id, value }));
          await Promise.all([updateDueDatePromise, updateEmbargoDatePromise]);
        } else {
          await dispatch(updateJobDueDate({ id, value }));
        }
      } catch (e: any) {
        error(e.message);
      }
      return;
    }

    dispatch(updateJobEmbargoDate({ id, value }))
      .unwrap()
      .catch((e: any) => error(e.message));
  };

  const onOrderClick = () => {
    dispatch(setCurrentTable('orders'));
    dispatch(clearFilters(undefined));
    dispatch(applyFilter({ type: 'filter_id', selections: [order.hid, 'order_hid_filter'] }));
  };

  const displaySeries = title?.entity_type === 'program';
  const breadcrumbTitle = displaySeries ? `${title.lineage_meta?.series_name} ${getLineageMetaTitle(title)}` : description;

  useEffect(function setCurrentTableKey() {
    dispatch(setCurrentTable('jobs'));
  }, []);

  useEffect(
    function setRefreshInterval() {
      if (status !== 'failed' && status !== 'in_error' && status !== 'complete') {
        const refreshInterval = setInterval(() => {
          dispatch(getJob({ id }));
        }, 60000);
        return () => clearInterval(refreshInterval);
      }
    },
    [tasks, status]
  );

  return (
    <Wrapper loading={loading}>
      <EntityActionDialog
        id={id}
        entity="job"
        action={dialogAction}
        overrideActions={actions}
        isOpen={!!dialogAction}
        setIsOpen={setDialogAction}
        afterSubmit={() => dispatch(getJob({ id }))}
      />
      <CancelJobDialog
        isOpen={isCancelDialogOpen}
        setIsOpen={setIsCancelDialogOpen}
        jobId={id}
        afterSubmit={() => {
          dispatch(getJob({ id }));
        }}
      />
      <OrderBar container justifyContent="space-between" alignItems="center">
        <div>
          <Breadcrumbs aria-label="breadcrumb">
            <Link to={fromOrderPage ? '/orders' : '/jobs'}>{fromOrderPage ? 'Orders' : 'Jobs'}</Link>
            <div>{insertWordBreaks(breadcrumbTitle) || ''}</div>
          </Breadcrumbs>
        </div>
        <ButtonWrapper>
          {canWriteJobs && actions && !!Object.keys(actions).length && (
            <>
              {Object.keys(actions).map((action) => {
                if (!isActionValid(action)) return;
                const currentAction = actions[action];
                return (
                  <Tooltip key={action} title={`${currentAction ? displayBulkActionIdent(currentAction.ident) : ''}`}>
                    <IconButton color="primary" onClick={() => setDialogAction(action)} size="small">
                      <PolarisIcon
                        icon={
                          currentAction.icon ||
                          bulkActionIcons?.[currentAction.ident] ||
                          (IconDictionary[currentAction.ident] && typeof IconDictionary[currentAction.ident] === 'string'
                            ? IconDictionary[currentAction.ident]
                            : 'edit')
                        }
                        height={19}
                      />
                    </IconButton>
                  </Tooltip>
                );
              })}
              {Object.keys(actions).find((a) => isActionValid(a)) && <Divider sx={{ opacity: 0.3 }} orientation="vertical" color="grey" flexItem />}
            </>
          )}
          {canWriteJobs && <Notes data={data} type="jobs" override />}
          {canWriteJobs && (!actions || !Object.keys(actions).length) && (
            <Tooltip
              title={
                status === 'complete' ? "Completed jobs can't be cancelled" : status === 'cancelled' ? 'This job is already cancelled' : 'Cancel Job'
              }
            >
              <div>
                <IconButton onClick={() => setIsCancelDialogOpen(true)} size="small" sx={{ color: 'default.main' }} disabled={disableCancelJob}>
                  <PolarisIcon icon="cancel" height={19} />
                </IconButton>
              </div>
            </Tooltip>
          )}
          <Refresh className="refresh-job" onClick={onRefresh} />
        </ButtonWrapper>
      </OrderBar>
      <Grid container spacing={2} wrap="nowrap" alignItems="center" justifyContent="space-between">
        <Grid item>
          <LabelValueGrid container rowSpacing={2} columnSpacing={6}>
            <LabelValue label="Order ID" color="primary">
              <LinkButton onClick={onOrderClick} to={`/orders`}>
                {order?.hid || '-'}
              </LinkButton>
            </LabelValue>
            <LabelValue label="Job ID" color="primary">
              {hid}
            </LabelValue>
            {typeof bundle_job_hid !== 'undefined' && (
              <LabelValue label="Job Bundle ID" color="primary">
                <BundleIdBox bundleId={bundle_job_hid} details />
              </LabelValue>
            )}
            <JobTypeLabelValue label="Job Type" color="primary">
              <JobTypeLabel
                type={job_type}
                style={{
                  // for alignment reasons
                  transform: 'scale(.85)',
                  transformOrigin: 'top left',
                  marginRight: -15
                }}
              />
            </JobTypeLabelValue>
            <LabelValue label="Customer" color="primary">
              {customer}
            </LabelValue>
            <LabelValue label="Profile" color="primary">
              <Grid container alignItems="center" sx={{ minHeight: 14 }}>
                {profile?.id && canViewProfiles ? (
                  <LinkButton to={`/profiles/${profile.id}`}>{findandFormatUTCDateInString(profile.name)}</LinkButton>
                ) : (
                  findandFormatUTCDateInString(profile?.name)
                )}
                {profile?.deleted_at && (
                  <Tooltip title="Profile has been deleted" arrow>
                    <Box sx={{ opacity: 0.3, ml: 1, height: 16 }}>
                      <PolarisIcon height={16} icon="error_circle" />
                    </Box>
                  </Tooltip>
                )}
              </Grid>
            </LabelValue>
            <LabelValue label="Managed By" color="primary">
              <AssigneeSelect value={managed_by as UserProfile} viewOnly assigns="user" emptyText="-" />
            </LabelValue>
            {/* CUSTOM FIELDS FROM jobDetails CONFIG */}
            {customFields?.map((customField: any) => (
              <LabelValue key={customField.key} label={customField.label} color="primary">
                {customField.render ? customField.render(data) : data?.[customField.key as keyof JobDetail]}
              </LabelValue>
            ))}
            <LabelValue label="Created at" color="primary">
              <DateTimeDisplay singleLine>{created_at}</DateTimeDisplay>
            </LabelValue>
            {!!embargo_date && !embargo_resumed_at && (
              <EditableDate
                label={t('Embargo Date')}
                key="embargo_date"
                color="primary"
                onEdit={onJobEdit}
                onEditFormatter={(date: Date) => date.toISOString()}
                disabled={!canWriteJobs || isEmbargoDateDisabled}
                onEditKey="embargo_date"
                value={embargo_date}
                maxDate={new Date(due_date)}
                filterTime={(selectedDate) => filterEmbargoMinAndMaxTime(selectedDate, due_date)}
                disablePastDates
              >
                <DateTimeDisplay singleLine>{embargo_date}</DateTimeDisplay>
              </EditableDate>
            )}
            {!!embargo_date && !!embargo_resumed_at && (
              <LabelValue label="Embargo Resumed" color="primary">
                <Tooltip
                  title={
                    <Typography fontSize=".86rem">
                      Embargo Date:
                      <DateTimeDisplay singleLine style={{ marginTop: '.25em' }}>
                        {embargo_date}
                      </DateTimeDisplay>
                    </Typography>
                  }
                >
                  <div>
                    <DateTimeDisplay singleLine>{embargo_resumed_at}</DateTimeDisplay>
                  </div>
                </Tooltip>
              </LabelValue>
            )}
            <EditableDate
              label="Due Date"
              key="due_date"
              color="primary"
              onEdit={onJobEdit}
              onEditFormatter={(date: Date) => date.toISOString()}
              disabled={!canWriteJobs}
              onEditKey="due_date"
              value={due_date}
            >
              <DateTimeDisplay singleLine>{due_date}</DateTimeDisplay>
            </EditableDate>
          </LabelValueGrid>
        </Grid>
        <Grid item sx={{ pr: 3 }}>
          <StyledJobStatus stage={stage} status={status} reason={status_reason} />
        </Grid>
      </Grid>
    </Wrapper>
  );
};

export default JobTitleBar;

const JobTypeLabelValue = styled(LabelValue)`
  margin-bottom: -10px;
`;

const Wrapper = styled(TitleBar)`
  ${({ theme }) => convertStylesToStyled(theme.customOverrides.JobTitleBar)}
`;

const OrderBar = styled(Grid)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  position: relative;

  & > nav {
    width: 100%;
  }

  & .MuiBreadcrumbs-ol {
    padding-right: ${({ theme }) => theme.spacing(2)};
  }
`;

const LabelValueGrid = styled(Grid)`
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.spacing(1)};

  & .MuiTypography-body2 {
    display: inline-flex;
    align-items: center;
    ${({ theme }) => convertStylesToStyled(theme.typography.body2)};
  }
`;

const StyledJobStatus = styled(StatusDisplay)`
  margin-left: ${({ theme }) => theme.spacing(1)};
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-right: 20px;
  column-gap: 8px;
`;
